import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CertificationStatus } from './certification.models';
import {
  OVERRIDE_EXPIRY_MESSAGE, certificationBadgeClass, certificationLabel, isOverrideExpiryRejection,
  overrideExpiryIsValid, requirementNeedsAttention,
} from './certification-copy';
import { CertificationService } from './certification.service';

@Component({
  selector: 'app-certification-status-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './certification-status-panel.component.html',
})
export class CertificationStatusPanelComponent implements OnChanges {
  @Input({ required: true }) courseId = '';
  @Input() userId?: string;
  @Input() instructorMode = false;
  @Input() compact = false;

  /**
   * KAN-560: raised after the server CONFIRMS an issue / override /
   * remove-override. List views listen so they can re-fetch the affected batch
   * rather than optimistically inventing a new eligibility locally.
   */
  @Output() statusChanged = new EventEmitter<CertificationStatus>();

  private service = inject(CertificationService);
  /**
   * KAN-560: this app is ZONELESS, and every field this component renders from
   * is a plain property mutated inside an HTTP callback. Without an explicit
   * markForCheck the view is never marked dirty, so the panel sat on its
   * skeleton — or on a bare "Certification" heading — until an unrelated click
   * elsewhere happened to run change detection and paint data that had already
   * arrived. QA reproduced that on three separate surfaces.
   *
   * EVERY callback below marks, error paths included: a swallowed error that
   * clears `loading` without a repaint leaves a PERMANENT spinner, which is
   * worse than the message it was meant to replace.
   */
  private cdr = inject(ChangeDetectorRef);
  status: CertificationStatus | null = null;
  loading = false;
  submitting = false;
  error = '';
  /**
   * KAN-625: failures of a dialog action (save / remove override) belong IN the
   * dialog. Writing them to `error` swapped the whole panel for a load-error
   * alert behind the still-open dialog.
   */
  dialogError = '';
  overrideDialog = false;
  removeDialog = false;
  reason = '';
  expiresAt = '';

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['courseId'] || changes['userId']) && this.courseId) this.load();
  }

  load(): void {
    this.loading = true; this.error = '';
    this.service.getStatus(this.courseId, this.userId).subscribe({
      next: status => { this.status = status; this.loading = false; this.cdr.markForCheck(); },
      error: () => { this.loading = false; this.error = "Couldn't load certification status."; this.cdr.markForCheck(); },
    });
  }

  label(): string { return this.status ? certificationLabel(this.status) : '' ; }
  badge(): string { return this.status ? certificationBadgeClass(this.status) : 'badge-ghost'; }
  needsAttention(requirement: CertificationStatus['requirements'][number]): boolean { return requirementNeedsAttention(requirement); }
  metCount(): number { return this.status?.requirements.filter(item => item.satisfied).length || 0; }

  issue(): void {
    if (!this.status || !this.userId) return;
    this.submitting = true; this.error = '';
    this.service.reevaluate(this.courseId, this.userId).subscribe({
      next: status => { this.status = status; this.submitting = false; this.cdr.markForCheck(); this.statusChanged.emit(status); },
      error: () => { this.submitting = false; this.error = "Couldn't issue the certificate. Nothing changed."; this.cdr.markForCheck(); },
    });
  }

  openOverrideDialog(): void { this.reason = ''; this.expiresAt = ''; this.dialogError = ''; this.overrideDialog = true; }
  openRemoveDialog(): void { this.reason = ''; this.dialogError = ''; this.removeDialog = true; }
  closeDialog(): void { this.overrideDialog = false; this.removeDialog = false; this.dialogError = ''; }

  /** KAN-625: any change to the expiry withdraws a message about the previous value. */
  onExpiryChange(value: string): void { this.expiresAt = value; this.dialogError = ''; }

  saveOverride(): void {
    if (!this.userId || this.reason.trim().length < 10) return;
    // KAN-625: this used to return silently, leaving the instructor with a
    // dialog that ignored the click.
    if (!overrideExpiryIsValid(this.expiresAt)) { this.dialogError = OVERRIDE_EXPIRY_MESSAGE; return; }
    this.submitting = true; this.dialogError = '';
    this.service.saveOverride(this.courseId, this.userId, this.reason.trim(), this.expiresAt || undefined).subscribe({
      next: status => { this.status = status; this.submitting = false; this.overrideDialog = false; this.reason = ''; this.expiresAt = ''; this.cdr.markForCheck(); this.statusChanged.emit(status); },
      error: err => {
        this.submitting = false;
        this.dialogError = isOverrideExpiryRejection(err) ? OVERRIDE_EXPIRY_MESSAGE : "Couldn't save the override. Nothing changed.";
        this.cdr.markForCheck();
      },
    });
  }

  removeOverride(): void {
    if (!this.userId || this.reason.trim().length < 10) return;
    this.submitting = true; this.dialogError = '';
    this.service.removeOverride(this.courseId, this.userId, this.reason.trim()).subscribe({
      next: status => { this.status = status; this.submitting = false; this.removeDialog = false; this.reason = ''; this.cdr.markForCheck(); this.statusChanged.emit(status); },
      error: () => { this.submitting = false; this.dialogError = "Couldn't remove the override. Nothing changed."; this.cdr.markForCheck(); },
    });
  }
}
